/**
 * Acting-society plumbing for the Bero platform console. A platform staffer
 * (SUPPORT/SUPER_ADMIN) has no society_id of their own, so to help a society
 * via the ordinary society-admin console they "enter" one from
 * /platform/society/[id] — from then on every API call must operate on THAT
 * society, not theirs. Module store (not React state) so the api-client can
 * read it synchronously per-request via setHeaderProvider, with a
 * sessionStorage mirror on web only (per-tab, cleared on tab close — a
 * platform staffer's acting context should never survive across devices or
 * quietly leak into a new tab) so a reload keeps the acting context instead
 * of silently dropping the staffer back to the denied gate mid-task.
 */
import React from 'react';
import { Platform } from 'react-native';


const KEY = 'bero.acting_society';

export type ActingSociety = { id: number; name: string; role: string };

function hasSessionStorage(): boolean {
  return Platform.OS === 'web' && typeof sessionStorage !== 'undefined';
}

function persist(v: ActingSociety | null) {
  if (!hasSessionStorage()) return;
  try {
    if (v) sessionStorage.setItem(KEY, JSON.stringify(v));
    else sessionStorage.removeItem(KEY);
  } catch {
    // storage unavailable (privacy mode etc.) — acting context just won't survive a reload
  }
}

function rehydrate(): ActingSociety | null {
  if (!hasSessionStorage()) return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<ActingSociety>;
    if (typeof v.id === 'number' && typeof v.name === 'string' && typeof v.role === 'string') {
      return { id: v.id, name: v.name, role: v.role };
    }
    return null;
  } catch {
    return null;
  }
}

let current: ActingSociety | null = rehydrate();
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

export function setActingSociety(id: number, name: string, role: string) {
  current = { id, name, role };
  persist(current);
  notify();
}

export function clearActingSociety() {
  current = null;
  persist(null);
  notify();
}

export function getActingSociety(): ActingSociety | null {
  return current;
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot() {
  return current;
}

export function useActingSociety(): ActingSociety | null {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// The X-Bero-Society header is now registered by lib/society-header.ts (the
// single coordinator that also handles the resident switcher). This module just
// holds the acting state + getActingSociety() for that coordinator to read.
